// @ts-nocheck
import React, { useState, useEffect } from "react";
import {
  fmtDateTime,
  apiFetchInit,
  CANDIDATE_RESCHEDULE_REASONS,
} from "@/legacy/helpersModule";
import { Badge } from "@/shared/components/ui/Badge";
import { Button, Field, Select, Textarea, useToast } from "@/shared/components/ui";
import { Modal } from "@/shared/components/ui/Modal";
import { useEscapeKey } from "@/shared/hooks/useEscapeKey";
import { HrNotificationEmailBanner } from "@/features/hr/components/HrNotificationEmailBanner";

const REASON_LABELS = CANDIDATE_RESCHEDULE_REASONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

export function RescheduleQueue({ onBack, onResolved }) {
  const toast = useToast();
  useEscapeKey(onBack);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [decision, setDecision] = useState("approve");
  const [hrNotes, setHrNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => {
    setLoading(true);
    fetch("/api/admin/reschedule-pending", apiFetchInit())
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setItems(d.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const onRealtime = () => load();
    window.addEventListener("swarhr:realtime", onRealtime);
    return () => window.removeEventListener("swarhr:realtime", onRealtime);
  }, []);
  const submit = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      const r = await fetch(
        `/api/admin/applications/${sel.applicationId}/reschedule-resolve`,
        apiFetchInit({
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ decision, hrNotes: hrNotes.trim() }),
        }),
      );
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.show({ tone: "danger", title: "Request failed", message: e.error || "Could not resolve the reschedule request." });
      } else {
        setSel(null);
        setHrNotes("");
        load();
        if (typeof onResolved === "function") await onResolved();
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-dvh safe-bottom bg-surface-alt">
      <div className="bg-white border-b border-line text-ink-900 px-4 sm:px-6 py-3 sm:py-4 safe-top safe-x flex items-center gap-3">
        <button type="button" onClick={onBack} className="text-ink-400 hover:text-ink-900 text-sm">← Back</button>
        <span className="font-bold">Reschedule approvals</span>
        <Badge />
      </div>
      <HrNotificationEmailBanner requestType="reschedule" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 safe-x">
        {loading ? <p className="text-ink-400 text-sm">Loading…</p> : items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-line p-8 text-center text-ink-400 text-sm">No pending reschedule requests.</div>
        ) : (
          <div className="space-y-3">
            {items.map((row) => (
              <button
                key={row.applicationId}
                type="button"
                onClick={() => { setSel(row); setDecision("approve"); setHrNotes(""); }}
                className="w-full text-left bg-white rounded-2xl border border-line p-4 hover:border-brand-300 transition-colors"
              >
                <p className="font-bold text-ink-900">{row.candidateName}</p>
                <p className="text-xs text-ink-400">{row.candidateEmail}</p>
                <p className="text-sm text-brand-700 font-semibold mt-1">{row.jobTitle || "—"} · Application #{row.applicationId}</p>
                {row.interviewScheduledAt ? <p className="text-xs text-ink-400 mt-1">Scheduled: {fmtDateTime(row.interviewScheduledAt)}</p> : null}
                <p className="text-xs text-ink-400 mt-1">Requested: {row.requestedAt ? fmtDateTime(row.requestedAt) : "—"}</p>
                {row.candidateReasonCode && <p className="text-xs text-ink-600 mt-2">Reason: {REASON_LABELS[row.candidateReasonCode] || row.candidateReasonCode}{row.candidateReasonText ? ` — ${row.candidateReasonText.slice(0, 200)}` : ""}</p>}
              </button>
            ))}
          </div>
        )}
        {sel && (
          <Modal
            title="Resolve reschedule"
            onClose={busy ? undefined : () => setSel(null)}
          >
            <p className="text-sm text-ink-600 mb-4">{sel.candidateName} · {sel.jobTitle}</p>
            {sel.candidateReasonCode ? (
              <div className="bg-surface-alt border border-line rounded-xl p-3 mb-4">
                <p className="text-xs font-bold text-ink-400 uppercase mb-1.5">Candidate request</p>
                <p className="text-sm text-ink-900 leading-snug">
                  {REASON_LABELS[sel.candidateReasonCode] || sel.candidateReasonCode}
                  {sel.candidateReasonText ? ` — ${String(sel.candidateReasonText).trim()}` : ""}
                </p>
              </div>
            ) : null}
            <div className="space-y-3 mb-4">
              <Field label="Decision">
                <Select value={decision} onChange={(e) => setDecision(e.target.value)}>
                  <option value="approve">Approve (candidate can continue the interview right away)</option>
                  <option value="reject">Reject</option>
                </Select>
              </Field>
              {decision === "approve" ? (
                <p className="text-xs text-ink-400 bg-surface-alt border border-line rounded-xl p-3">On approval, the missed slot is cleared and the candidate can resume the interview immediately — no new time is scheduled.</p>
              ) : null}
              <Field label="Notes (optional)">
                <Textarea value={hrNotes} onChange={(e) => setHrNotes(e.target.value)} rows={3} placeholder="Internal notes…" />
              </Field>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button type="button" variant="secondary" disabled={busy} onClick={() => setSel(null)} className="w-full sm:flex-1">Cancel</Button>
              <Button type="button" loading={busy} onClick={submit} className="w-full sm:flex-1">Submit</Button>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
}
